import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { searchPromotionalBannerProductSelector } from 'modules/search/selectors';
import { HeaderPageWrapper } from 'components/templates/HeaderPageWrapper';
import { AggregationHero } from 'components/organisms/AggregationHero';
import { StandaloneLinkList } from 'components/organisms/StandaloneLinkList';
import { ContentCardList } from 'components/organisms/ContentCardList';
import { ExternalAdBanner, ExternalAdBannerType } from 'components/organisms/ExternalAdBanner/ExternalAdBanner';
import { AggregationHeader, FontSizes } from 'components/molecules/AggregationHeader';
import { Footer } from 'components/molecules/Footer';
import { Container } from 'components/atoms/Container/Container';
import { Grid } from 'components/atoms/Grid/Grid';
import { Divider } from 'components/atoms/Divider/Divider';
import { PageHelmet } from 'components/atoms/PageHelmet/PageHelmet';
import { CardListPlaceholder } from 'components/atoms/CardListPlaceholder/CardListPlaceholder';
import { PromotionalBanner } from 'components/molecules';
import { SocialSharing } from 'components/molecules/SocialSharing/SocialSharing';
import { Scroller } from 'utils';
import { productAggsPageTitles } from 'constants/index';
import { Product, Content } from 'mxp-schemas';

export interface PageProductAggregationOwnProps extends RouteComponentProps {
  isAggByTopicPage: boolean;
  productTypeSlug?: string;
  topicSlug?: string;
}

export interface PageProductAggregationProps extends PageProductAggregationOwnProps {
  isAuth: boolean | null;
  topicName: string;
  topics: State.TopicOption[] | null;
  topicsHash: State.TopicsHash | null;
  subtopics: State.Subtopic[];
  productTypeName: string;
  productTypesOrganicSorted: State.ProductType[];
  productTypesHash: State.ProductTypesHash | null;
  productAggPageContent?: State.ProductAggsPageContent;
  promotionalBannerProduct?: State.ContentCardItem | null;
  topicsSlugIdMap: { [key: string]: string };
  loading: boolean;
  skipContentFrontloadRequest: boolean;
  getProductAggsPageContent: () => Promise<void>;
  resetSkipContentFrontloadRequest: () => void;
  appInitialized: boolean;
}

export const PageProductAggregation: React.FC<PageProductAggregationProps> = React.memo(
  ({
    isAggByTopicPage,
    productTypeSlug = '',
    topicSlug = '',
    topicName,
    productTypeName,
    productAggPageContent,
    promotionalBannerProduct,
    subtopics,
    topics,
    productTypesOrganicSorted,
    productTypesHash,
    topicsHash,
    topicsSlugIdMap,
    loading,
  }) => {
    useGoogleAds(productAggPageContent?.externalAdsConfig);
    const targetContainerRef: any = React.useRef();
    const containerContentRef: any = React.useRef();
    const cpeExpress = useSelector(searchPromotionalBannerProductSelector);
    const isCpeExpressPublished = cpeExpress?.published;

    React.useEffect(() => {
      Scroller.scrollToTarget(targetContainerRef);
      Scroller.scrollToTarget(containerContentRef);
    }, [productAggPageContent]);

    const renderProductTypesList = React.useMemo(
      () => (
        <>
          <StyledAggregationHeader title="All Types" size={FontSizes.l} />
          <StandaloneLinkList
            topicSlug={topicSlug}
            itemList={productTypesOrganicSorted}
            isAggByTopicPage={isAggByTopicPage}
            isAggByProductTypePage={!isAggByTopicPage}
            isProductTypeLink
            productTypeSlug={productTypeSlug}
            key="product-types-list"
          />
        </>
      ),
      [topicSlug, productTypesOrganicSorted, productTypeSlug, isAggByTopicPage]
    );

    const renderPage = (): React.ReactNode => {
      return (
        <HeaderPageWrapper contentRef={targetContainerRef} contentInnerRef={containerContentRef}>
          {productAggPageContent?.externalAdsConfig?.topAdBanner && (
            <ExternalAdBanner type={ExternalAdBannerType.TOP} />
          )}
          <AggregationHero
            topicName={productAggPageContent?.title}
            hasShortcut={false}
            topicSummary={productAggPageContent?.summary}
            image={productAggPageContent?.image}
            isProductAggregationPage
          />

          <StyledContainer>
            <Grid stackable>
              <Grid.Row>
                <StyledGridColumn computer={4} tablet={16}>
                  {!isAggByTopicPage && (
                    <>
                      {renderProductTypesList}
                      <StyledDivider />
                    </>
                  )}
                  {Boolean(subtopics.length) && (
                    <>
                      <StyledAggregationHeader title="All Subtopics" size={FontSizes.l} />
                      <StandaloneLinkList
                        topicSlug={topicSlug}
                        itemList={subtopics}
                        isAggByTopicPage={isAggByTopicPage}
                        isAggByProductTypePage={!isAggByTopicPage}
                        isSubtopicLink
                        key="subtopic-list"
                      />
                      <StyledDivider />
                    </>
                  )}

                  <StyledAggregationHeader title="All Topics" size={FontSizes.l} />
                  <StandaloneLinkList
                    topicSlug={topicSlug}
                    itemList={topics}
                    isAggByTopicPage={isAggByTopicPage}
                    isAggByProductTypePage={!isAggByTopicPage}
                    key="topic-list"
                  />

                  {isAggByTopicPage && (
                    <>
                      <StyledDivider />
                      {renderProductTypesList}
                    </>
                  )}
                </StyledGridColumn>
                <Grid.Column computer={12} tablet={16}>
                  {Boolean(
                    isCpeExpressPublished && productTypeSlug === Product.ProductSlug.COURSE && promotionalBannerProduct
                  ) && (
                    <PromotionalBanner
                      title="Quality courses available anytime"
                      description="Customize your learning experience with topics that are relevant to your professional development and your jurisdictions. CPExpress is a comprehensive, easy-to-use online learning resource."
                      testId="cpexpress-promotional-banner"
                      titleTestId="cpexpress-promotional-title"
                      descriptionTestId="cpexpress-promotional-desc"
                      showBackground
                      isLeftAligned
                      product={promotionalBannerProduct}
                    />
                  )}
                  {loading ? (
                    <CardListPlaceholder />
                  ) : (
                    productAggPageContent?.mainSectionSlugs?.map((slug: string, index: number) => {
                      const item: any = productAggPageContent.mainSectionItems?.[index];
                      const promotionalComponent: any =
                        productAggPageContent?.mainSectionProductTypesAndPromotionalComponents?.[index];
                      const productTypeNameFromSlug: string = productTypesHash?.[slug || '']?.name || '';
                      const topicNameFromSlug: string = topicsHash?.[topicsSlugIdMap?.[slug || ''] || '']?.name || '';
                      const listTitle: string = isAggByTopicPage ? productTypeNameFromSlug : topicNameFromSlug;

                      if (
                        promotionalComponent?.contentType === Content.ContentType.PROMOTIONAL_COMPONENT &&
                        promotionalComponent?.header &&
                        promotionalComponent?.summary
                      ) {
                        return (
                          <PromotionalBanner
                            title={promotionalComponent?.header || ''}
                            description={promotionalComponent?.summary || ''}
                            testId="cpexpress-promotional-banner"
                            titleTestId="cpexpress-promotional-title"
                            descriptionTestId="cpexpress-promotional-desc"
                            showBackground
                            isLeftAligned
                            product={item?.[0]}
                          />
                        );
                      }
                      return (
                        Boolean(item.length && listTitle) && (
                          <ContentCardList
                            key={`${slug}-${index}`}
                            contentList={item}
                            topicSlug={topicSlug || slug}
                            cardListTitle={listTitle}
                            isProductAggregationPage
                            productTypeName={productTypeName || productTypeNameFromSlug}
                            topicName={topicName || topicNameFromSlug}
                            productTypeSlug={productTypeSlug || slug}
                          />
                        )
                      );
                    })
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </StyledContainer>
          {productAggPageContent?.externalAdsConfig?.bottomAdBanner && (
            <ExternalAdBanner type={ExternalAdBannerType.BOTTOM} />
          )}
          <Footer />
          <SocialSharing title={productAggPageContent?.metaTitle} isSticky />
        </HeaderPageWrapper>
      );
    };

    const pageTitle = productAggsPageTitles[productTypeSlug] || productTypeName;

    return (
      <>
        <PageHelmet
          title={`AICPA & CIMA Learning Products | ${topicName || pageTitle}`}
          meta={{
            title: productAggPageContent?.metaTitle,
            description: productAggPageContent?.metaDescription,
          }}
          openGraph={{
            title: productAggPageContent?.metaTitle,
            description: productAggPageContent?.metaDescription,
            image: productAggPageContent?.ogImage && `https:${productAggPageContent.ogImage}`,
          }}
        />
        <StyledDiv>{renderPage()}</StyledDiv>
      </>
    );
  }
);

const StyledContainer = styled(Container)`
  padding: ${props => props.theme.pxToRem(48)} 0;
`;
const StyledGridColumn = styled(Grid.Column)`
  ${props => props.theme.mediaQueries.mobileOnly} {
    order: 1;
  }
`;

const StyledAggregationHeader = styled(AggregationHeader)`
  margin-bottom: ${props => props.theme.pxToRem(16)};
  font-size: ${props => props.theme.pxToRem(18)};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const StyledDivider = styled(Divider)`
  &&& {
    margin-top: ${props => props.theme.pxToRem(39)};
    margin-bottom: ${props => props.theme.pxToRem(31)};
  }
`;

const StyledDiv = styled.div`
  ${props => props.theme.mediaQueries.desktopOnly} {
    height: ${props => props.theme.pxToRem(1)};
    min-height: 100%;
  }
`;
